<script lang="ts">
  import '../app.css';
  import type { Snippet } from 'svelte';
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import Banner from '$lib/components/Banner.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import PageState from '$lib/components/PageState.svelte';
  import { removeBootLoader } from '$lib/utils/bootLoader';
  import { authState, initAuth } from '$lib/auth/keycloak';
  import { fetchTopMenu } from '$lib/api/legacy-api';
  import { resolveLegacyPortalUrl } from '$lib/legacy-portal';
  import { toNavigationItems, type NavigationItem } from '$lib/navigation';

  let { children }: { children: Snippet } = $props();

  let menuItems = $state<NavigationItem[]>([]);
  let menuStatus = $state<'loading' | 'ready' | 'error'>('loading');
  let menuRequest: AbortController | undefined;
  let disposed = false;

  async function loadMenu() {
    menuRequest?.abort();
    const request = new AbortController();
    menuRequest = request;
    menuStatus = 'loading';
    try {
      const hostname = window.location.hostname;
      const items = await fetchTopMenu(hostname, request.signal);
      if (disposed || request.signal.aborted) {
        return;
      }
      const portalUrl = resolveLegacyPortalUrl(hostname);
      menuItems = portalUrl ? toNavigationItems(items, portalUrl) : [];
      menuStatus = 'ready';
    } catch {
      if (!disposed && !request.signal.aborted) {
        menuStatus = 'error';
      }
    }
  }

  onMount(() => {
    void initAuth(window.location.hostname).then(() => {
      if (disposed) {
        return;
      }
      removeBootLoader();
      if (get(authState).status === 'authenticated') {
        void loadMenu();
      }
    });
    return () => {
      disposed = true;
      menuRequest?.abort();
    };
  });
</script>

<div id="app" class="flex flex-col">
  <Banner {menuItems} {menuStatus} onRetry={() => void loadMenu()} />
  <main class="min-h-0 flex-1 overflow-hidden">
    {#if $authState.status === 'authenticated'}
      {@render children()}
    {:else if $authState.status === 'error'}
      <PageState
        title="Unable to authenticate"
        message="Unable to authenticate against the Service Portal for this environment."
      />
    {:else}
      <PageState title="Authenticating..." loading />
    {/if}
  </main>
  <Footer />
</div>
